import React from 'react';
import { BUTTON_CONFIG } from '../JRCButton/JRCButtonConfig';
import { ClickAwayListener } from '../Form/Common/ClickAwayListener';
import { JRCFlexBox } from '../JRCFlex/JRCFlexBox';
import { JRCConditionalWrapper } from '../Common/JRCConditionalWrapper';
import { JRCIcon } from '../JRCIcon/JRCIcon';
import type { JRCButtonDropdownOptionProps, JRCButtonDropdownProps } from './JRCButtonDropdown.types';
import { DropdownOption, MarginLessJRCButton, MenuWithoutTitle, MenuWithTitle } from './JRCButtonDropdown.styles';
import { limitSize } from '../../utils/utils.string';
import styled, { useTheme } from 'styled-components';

const StyledJRCFlexBox = styled(JRCFlexBox)<{ open: boolean }>`
    position: relative;
    z-index: ${(props) => (props.open ? 11 : 0)};
`;

export const JRCButtonDropdown = (props: JRCButtonDropdownProps) => {
    const {
        icon = 'icon-angle-down',
        iconOpen = 'icon-angle-up',
        color = 'primary',
        variant = 'contained',
        small = false,
        smallOpenDirection = 'right',
        optionSelected = false,
        label,
        options,
        onClick,
        disabled,
    } = props;
    const theme = useTheme();

    const themeButton = { ...BUTTON_CONFIG[variant][disabled ? 'disabled' : color] };

    const [open, setOpen] = React.useState(false);

    const safeClick = (option: JRCButtonDropdownOptionProps) => {
        option.onClick ? option.onClick() : undefined;
        setOpen(false);
    };

    if (props.semiTransparentClosed && !open && small) {
        themeButton.background = 'black20';
        themeButton.border = 'black20';
    }

    return (
        <ClickAwayListener
            onClickAway={() => setOpen(false)}
            as="span"
            position="relative"
            display="inline-block"
            margin=".2rem">
            <StyledJRCFlexBox gap={0} as="span" open={open} inline>
                {onClick ? (
                    <>
                        <MarginLessJRCButton
                            borderLeft
                            $themeButton={themeButton}
                            onClick={onClick}
                            open={open}
                            variant={variant}
                            disabled={disabled}
                            color={color}>
                            {label}
                        </MarginLessJRCButton>
                        <MarginLessJRCButton
                            aria-label="GLOBAL_OPEN"
                            borderRight
                            darker
                            $themeButton={themeButton}
                            icon={open ? iconOpen : icon}
                            onClick={() => setOpen((open) => !open)}
                            open={open}
                            variant={variant}
                            disabled={disabled}
                            color={color}
                        />
                    </>
                ) : (
                    <>
                        <MarginLessJRCButton
                            borderRight={small && smallOpenDirection === 'left' && open}
                            borderLeft={small && smallOpenDirection === 'right' && open}
                            $themeButton={themeButton}
                            icon={open ? iconOpen : icon}
                            iconRight
                            onClick={() => setOpen((open) => !open)}
                            open={open}
                            variant={variant}
                            disabled={disabled}
                            color={color}
                            aria-label={label}
                            darker={small && open}>
                            {!small && label}
                        </MarginLessJRCButton>
                    </>
                )}
            </StyledJRCFlexBox>
            {open && (
                <JRCConditionalWrapper
                    condition={small}
                    wrapper={(children) => (
                        <MenuWithTitle
                            direction={smallOpenDirection}
                            label={limitSize(label, 30)}
                            themeButton={themeButton}>
                            {children}
                        </MenuWithTitle>
                    )}
                    wrapperFalse={(children) => <MenuWithoutTitle>{children}</MenuWithoutTitle>}>
                    {options.map((option, index) => (
                        <DropdownOption
                            key={index}
                            data-cy={'dropdown_option_' + index}
                            as={option.href && !option.disabled ? 'a' : undefined}
                            href={option.href}
                            target={option.target}
                            disabled={option.disabled}
                            type="button"
                            optionSelected={optionSelected && option.label === label}
                            onClick={option.href && !option.disabled ? undefined : () => safeClick(option)}>
                            {option.icon ? (
                                <JRCIcon
                                    {...option.icon}
                                    size={
                                        ['circle', 'square'].includes(option.icon.variant || '')
                                            ? theme.size.l
                                            : theme.size.md
                                    }
                                />
                            ) : (
                                <></>
                            )}
                            {option.label}
                        </DropdownOption>
                    ))}
                </JRCConditionalWrapper>
            )}
        </ClickAwayListener>
    );
};
